import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import apiClient from "@/lib/apiClient";
import { useAuth } from "@/context/AuthContext";
import UsageBanner from "@/components/UsageBanner";
import { RiskBadge, StatusBadge, OutcomeBadge, scoreColor } from "@/lib/status";
import { FilePlus, TrendUp, ShieldWarning, Gauge, MagnifyingGlass, CurrencyDollar, ChartLineUp } from "@phosphor-icons/react";

function Stat({ label, value, sub, icon: Icon, accent }) {
  return (
    <div className="bg-white border border-slate-200 rounded-lg p-5">
      <div className="flex items-center justify-between">
        <span className="font-mono-plex text-[10px] uppercase tracking-[0.2em] font-semibold text-slate-400">{label}</span>
        <Icon size={18} color={accent} weight="bold" />
      </div>
      <div className="font-display font-black text-3xl tracking-tighter mt-3 text-[#0F172A]">{value}</div>
      {sub && <div className="text-xs text-slate-400 mt-1">{sub}</div>}
    </div>
  );
}

export default function DashboardPage() {
  const navigate = useNavigate();
  const { refreshCompany } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");

  useEffect(() => {
    apiClient.get("/proposals").then((r) => setItems(r.data)).finally(() => setLoading(false));
    refreshCompany().catch(() => {});
  }, [refreshCompany]);

  const filtered = items.filter((i) =>
    i.project_name.toLowerCase().includes(q.toLowerCase()) || i.client_name.toLowerCase().includes(q.toLowerCase()));
  const done = items.filter((i) => i.review_status === "complete");
  const avgIq = done.length ? Math.round(done.reduce((a, b) => a + (b.proposal_iq_score || 0), 0) / done.length) : 0;
  const highRisk = items.filter((i) => ["HIGH", "CRITICAL"].includes(i.risk_level)).length;
  const parseMoney = (s) => { const m = String(s || "").replace(/[^0-9.]/g, ""); return m ? parseFloat(m) : 0; };
  const totalProfit = done.reduce((a, b) => a + parseMoney(b.profit_opportunity), 0);
  const marginVals = done.map((b) => parseMoney(b.gross_margin)).filter((x) => x > 0);
  const avgMargin = marginVals.length ? Math.round(marginVals.reduce((a, b) => a + b, 0) / marginVals.length) : 0;

  const renderBody = () => {
    if (loading) return <tr><td colSpan={9} className="px-4 py-12 text-center text-slate-400">Loading…</td></tr>;
    if (filtered.length === 0) return <tr><td colSpan={9} className="px-4 py-16 text-center text-slate-400" data-testid="empty-state">No proposals yet. Upload your first one.</td></tr>;
    return filtered.map((i) => (
      <tr key={i.id} data-testid={`proposal-row-${i.id}`} onClick={() => navigate(`/review/${i.id}`)}
        className="border-b border-slate-100 hover:bg-slate-50 cursor-pointer transition-colors">
        <td className="px-4 py-3 font-semibold text-[#0F172A] whitespace-nowrap">{i.project_name}</td>
        <td className="px-4 py-3 text-slate-600 whitespace-nowrap">{i.client_name}</td>
        <td className="px-4 py-3 text-slate-600 font-mono-plex whitespace-nowrap">${(Number(i.proposal_value) || 0).toLocaleString()}</td>
        <td className="px-4 py-3 text-slate-500 whitespace-nowrap">{new Date(i.date_reviewed).toLocaleDateString()}</td>
        <td className="px-4 py-3 font-display font-bold whitespace-nowrap" style={{ color: i.review_status === "complete" ? scoreColor(i.proposal_iq_score) : "#94A3B8" }}>{i.review_status === "complete" ? i.proposal_iq_score : "—"}</td>
        <td className="px-4 py-3">{i.review_status === "complete" ? <RiskBadge value={i.risk_level} /> : <span className="text-slate-400 text-xs">—</span>}</td>
        <td className="px-4 py-3 text-slate-600 max-w-[140px] truncate">{i.review_status === "complete" ? i.gross_margin : "—"}</td>
        <td className="px-4 py-3">{i.review_status === "processing" ? <StatusBadge value="Analyzing" /> : i.review_status === "failed" ? <StatusBadge value="Failed" /> : <StatusBadge value="Analysis Complete" />}</td>
        <td className="px-4 py-3">{i.review_status === "complete" ? <OutcomeBadge value={i.outcome || "pending"} testId={`outcome-badge-${i.id}`} /> : <span className="text-slate-400 text-xs">—</span>}</td>
      </tr>
    ));
  };

  return (
    <div className="space-y-8">
      <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4">
        <div>
          <h1 className="font-display font-black text-4xl md:text-5xl tracking-tighter text-[#0F172A]">My Proposals</h1>
          <p className="font-mono-plex text-[10px] uppercase tracking-[0.25em] text-blue-500 font-semibold mt-2">Find the problems before they become projects.</p>
        </div>
        <button data-testid="new-review-cta" onClick={() => navigate("/new")} className="inline-flex items-center gap-2 h-11 px-5 bg-[#0F172A] hover:bg-[#1E293B] text-white rounded-md font-semibold text-sm transition-colors">
          <FilePlus size={17} weight="bold" /> Analyze New Proposal
        </button>
      </div>

      <UsageBanner />

      <div className="rounded-lg bg-black text-white grain p-6 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4" data-testid="profit-headline">
        <div>
          <div className="flex items-center gap-2">
            <CurrencyDollar size={18} weight="fill" className="text-blue-400" />
            <span className="font-mono-plex text-[10px] uppercase tracking-[0.25em] font-semibold text-slate-400">Potential Profit Identified</span>
          </div>
          <div className="font-display font-black text-4xl md:text-5xl tracking-tighter mt-2" data-testid="profit-total">
            ${totalProfit.toLocaleString()}
          </div>
          <div className="text-slate-400 text-sm mt-1">Margin, labor and scope exposure Proposal Forensics found across {done.length} analyzed proposal{done.length !== 1 ? "s" : ""}.</div>
        </div>
        <div className="text-right shrink-0">
          <div className="font-display font-black text-3xl tracking-tighter text-emerald-400">{avgMargin ? `${avgMargin}%` : "—"}</div>
          <div className="font-mono-plex text-[10px] uppercase tracking-[0.2em] text-slate-500 mt-1">Avg Gross Margin</div>
        </div>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Stat label="Proposals Analyzed" value={items.length} icon={TrendUp} accent="#0F172A" />
        <Stat label="Avg Forensics Score" value={avgIq} sub="out of 100" icon={Gauge} accent={scoreColor(avgIq)} />
        <Stat label="Avg Gross Margin" value={avgMargin ? `${avgMargin}%` : "—"} sub="across proposals" icon={ChartLineUp} accent="#2563EB" />
        <Stat label="High-Risk Proposals" value={highRisk} sub="need attention" icon={ShieldWarning} accent="#DC2626" />
      </div>

      <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
        <div className="p-4 border-b border-slate-200 flex items-center gap-3">
          <MagnifyingGlass size={18} className="text-slate-400" />
          <input data-testid="dashboard-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search by project or client…" className="flex-1 text-sm bg-transparent focus:outline-none" />
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead><tr className="border-b border-slate-200 bg-slate-50 text-left">
              {["Project", "Client", "Value", "Analyzed", "Forensics Score", "Risk", "Margin", "Status", "Outcome"].map((h) => (
                <th key={h} className="px-4 py-3 font-mono-plex text-[10px] uppercase tracking-[0.15em] font-semibold text-slate-500 whitespace-nowrap">{h}</th>
              ))}
            </tr></thead>
            <tbody>{renderBody()}</tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
